import "./App.css";

import { Link } from "react-router-dom";
import { useState } from "react";
import { WEB_APP_URL } from "./config";

function PrivacyPolicy() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="bg-black text-gray-200">
      {/* Navbar */}
      <header className="bg-black bg-opacity-70 backdrop-blur-lg sticky top-0 w-full z-50 shadow-lg">
        <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <img
              src="/images/logo.png"
              alt="Fit-Match Logo"
              className="h-16 w-auto"
            />
            <Link to="/" className="text-2xl font-bold gradient-text">
              FIT-MATCH
            </Link>
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/#how-it-works" className="nav-link font-medium">
              How It Works
            </Link>
            <Link to="/our-story" className="nav-link font-medium">
              Our Story
            </Link>
            <Link to="/professionals" className="nav-link font-medium">
              Pros
            </Link>
            <Link to="/training-providers" className="nav-link font-medium">
              Providers
            </Link>
            <Link to="/communities" className="nav-link font-medium">
              Communities
            </Link>
            <Link to="/facilities" className="nav-link font-medium">
              Facilities
            </Link>
            <a
              href={WEB_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link font-medium px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white"
            >
              Login
            </a>
          </div>
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="text-white focus:outline-none"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16m-4 6h4"
                ></path>
              </svg>
            </button>
          </div>
        </nav>
        {/* Mobile Menu */}
        <div
          className={`${
            isMobileMenuOpen ? "block" : "hidden"
          } md:hidden bg-black bg-opacity-90 py-4`}
        >
          <Link
            to="/#how-it-works"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            How It Works
          </Link>
          <Link
            to="/our-story"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Our Story
          </Link>
          <Link
            to="/professionals"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Pros
          </Link>
          <Link
            to="/training-providers"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Providers
          </Link>
          <Link
            to="/communities"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Communities
          </Link>
          <Link
            to="/facilities"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Facilities
          </Link>
          <a
            href={WEB_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Login
          </a>
        </div>
      </header>

      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-12 md:py-16 text-center relative overflow-hidden bg-gray-950">
          <div className="container mx-auto px-6 relative z-10">
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight tracking-tight">
              Privacy <span className="gradient-text">Policy</span>
            </h1>
            <p className="mt-4 text-lg md:text-xl max-w-2xl mx-auto text-gray-400">
              Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </section>

        {/* Privacy Policy Content */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-6 max-w-4xl">
            <div className="prose prose-invert max-w-none">
              <p className="text-lg text-gray-300 mb-8">
                Fit‑Match ("we", "our", or "us") operates the Fit‑Match mobile
                application and the website https://www.fit-match.co.uk (together,
                the "Service"). This Privacy Policy explains how we collect, use,
                disclose, and safeguard your information when you use our Service.
              </p>
              <p className="text-lg text-gray-300 mb-12">
                By using Fit‑Match, you agree to the collection and use of
                information in accordance with this Privacy Policy.
              </p>

              <div className="space-y-12">
                {/* Section 1 */}
                <div>
                  <h2 className="text-3xl font-bold mb-4 text-white">
                    1. Information We Collect
                  </h2>
                  <p className="text-lg text-gray-300 mb-4">
                    We collect information that you provide directly to us,
                    information collected automatically when you use the Service,
                    and information related to payments.
                  </p>
                  <h3 className="text-2xl font-semibold mb-3 text-blue-fit-match mt-6">
                    A. Personal Information You Provide
                  </h3>
                  <p className="text-lg text-gray-300 mb-4">
                    We may collect and process the following data, which may be
                    linked to your identity:
                  </p>
                  <div className="ml-4 space-y-4">
                    <div>
                      <h4 className="text-xl font-semibold mb-2 text-white">
                        Contact Information
                      </h4>
                      <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                        <li>Name</li>
                        <li>Email address</li>
                        <li>Phone number</li>
                        <li>Physical address</li>
                        <li>
                          Other user contact information (e.g. trainer or client
                          contact details)
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold mb-2 text-white">
                        User Content
                      </h4>
                      <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                        <li>Photos or videos you upload</li>
                        <li>Messages, emails, or text messages sent through the app</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold mb-2 text-white">
                        Financial Information
                      </h4>
                      <p className="text-gray-300">
                        Payment information (such as card details or billing
                        information)
                      </p>
                      <p className="text-gray-400 text-sm mt-2">
                        Payment details are processed securely by third‑party
                        payment providers. Fit‑Match does not store full card
                        details on its own servers.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section 2 */}
                <div>
                  <h2 className="text-3xl font-bold mb-4 text-white">
                    2. How We Use Your Information
                  </h2>
                  <p className="text-lg text-gray-300 mb-4">
                    We use the information we collect for the following purposes:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li>To provide, operate, and maintain the Fit‑Match Service</li>
                    <li>To create and manage user accounts</li>
                    <li>To enable matching between users and fitness professionals</li>
                    <li>To facilitate communication between users</li>
                    <li>To process payments and subscriptions</li>
                    <li>To provide customer support</li>
                    <li>
                      To improve app performance, features, and user experience
                    </li>
                    <li>To monitor usage and conduct analytics</li>
                    <li>To comply with legal obligations</li>
                  </ul>
                </div>

                {/* Section 3 */}
                <div>
                  <h2 className="text-3xl font-bold mb-4 text-white">
                    3. Data Used for Tracking
                  </h2>
                  <p className="text-lg text-gray-300 mb-4">
                    Some data may be used to track you across apps and websites
                    owned by other companies, in accordance with Apple's App
                    Tracking Transparency framework and applicable law.
                  </p>
                  <p className="text-lg text-gray-300 mb-4">
                    This may include:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                    <li>Contact information</li>
                    <li>Financial information</li>
                    <li>User‑generated content</li>
                  </ul>
                  <p className="text-lg text-gray-300 mt-4">
                    Where required, we will request your consent before engaging
                    in tracking activities.
                  </p>
                </div>

                {/* Section 4 */}
                <div>
                  <h2 className="text-3xl font-bold mb-4 text-white">
                    4. Data Sharing and Disclosure
                  </h2>
                  <p className="text-lg text-gray-300 mb-4 font-semibold">
                    We do not sell your personal data.
                  </p>
                  <p className="text-lg text-gray-300 mb-4">
                    We may share your information only in the following
                    circumstances:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li>
                      <strong className="text-white">Service Providers:</strong>{" "}
                      With trusted third‑party providers who help us operate the
                      app (e.g. hosting, analytics, payment processing)
                    </li>
                    <li>
                      <strong className="text-white">Legal Requirements:</strong>{" "}
                      If required to comply with legal obligations, court orders,
                      or law enforcement requests
                    </li>
                    <li>
                      <strong className="text-white">Business Transfers:</strong>{" "}
                      In connection with a merger, acquisition, or sale of assets
                    </li>
                  </ul>
                  <p className="text-lg text-gray-300 mt-4">
                    All third parties are required to protect your information
                    and use it only for the purposes specified by us.
                  </p>
                </div>

                {/* Section 5 */}
                <div>
                  <h2 className="text-3xl font-bold mb-4 text-white">
                    5. Data Retention
                  </h2>
                  <p className="text-lg text-gray-300">
                    We retain personal data only for as long as necessary to
                    fulfil the purposes outlined in this Privacy Policy, unless
                    a longer retention period is required or permitted by law.
                  </p>
                </div>

                {/* Section 6 */}
                <div>
                  <h2 className="text-3xl font-bold mb-4 text-white">
                    6. Your Rights
                  </h2>
                  <p className="text-lg text-gray-300 mb-4">
                    Depending on your location, you may have the following rights:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                    <li>The right to access your personal data</li>
                    <li>The right to correct inaccurate information</li>
                    <li>The right to request deletion of your data</li>
                    <li>The right to restrict or object to processing</li>
                    <li>The right to data portability</li>
                    <li>The right to withdraw consent at any time</li>
                  </ul>
                  <p className="text-lg text-gray-300 mt-4">
                    To exercise your rights, please contact us using the details
                    below.
                  </p>
                </div>

                {/* Section 7 */}
                <div>
                  <h2 className="text-3xl font-bold mb-4 text-white">
                    7. Data Security
                  </h2>
                  <p className="text-lg text-gray-300">
                    We implement appropriate technical and organisational
                    measures to protect your personal data. However, no method of
                    transmission over the Internet or electronic storage is 100%
                    secure.
                  </p>
                </div>

                {/* Section 8 */}
                <div>
                  <h2 className="text-3xl font-bold mb-4 text-white">
                    8. Children's Privacy
                  </h2>
                  <p className="text-lg text-gray-300">
                    Fit‑Match is not intended for use by individuals under the
                    age of 18. We do not knowingly collect personal data from
                    children.
                  </p>
                </div>

                {/* Section 9 */}
                <div>
                  <h2 className="text-3xl font-bold mb-4 text-white">
                    9. Changes to This Privacy Policy
                  </h2>
                  <p className="text-lg text-gray-300">
                    We may update this Privacy Policy from time to time. Any
                    changes will be posted on this page with an updated revision
                    date.
                  </p>
                </div>

                {/* Section 10 */}
                <div>
                  <h2 className="text-3xl font-bold mb-4 text-white">
                    10. Contact Us
                  </h2>
                  <p className="text-lg text-gray-300 mb-4">
                    If you have any questions about this Privacy Policy or our
                    data practices, please contact us:
                  </p>
                  <div className="bg-gray-800 p-6 rounded-lg">
                    <p className="text-gray-300">
                      <strong className="text-white">Email:</strong>{" "}
                      <a
                        href="mailto:support@fit-match.co.uk"
                        className="text-blue-fit-match hover:underline"
                      >
                        support@fit-match.co.uk
                      </a>
                    </p>
                    <p className="text-gray-300 mt-2">
                      <strong className="text-white">Website:</strong>{" "}
                      <a
                        href="https://www.fit-match.co.uk"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-fit-match hover:underline"
                      >
                        https://www.fit-match.co.uk
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 py-8 text-center text-gray-400">
        <p>&copy; 2024 Fit-Match. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;

